import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      eyebrow="Legal · Privacy"
      updated="December 2025"
    >
      <p className="text-lg text-zinc-300 font-light leading-relaxed">
        We collect as little as possible. We delete what we don't need. We
        never sell anything. This document tells you exactly what that means
        in practice.
      </p>

      <LegalSection n={1} title="Who we are">
        <p>
          Invisible System Design ("ISD", "we", "us") builds and operates
          conversation-native software that runs inside messaging platforms
          (iMessage, WhatsApp, Telegram, Slack) on behalf of our clients.
        </p>
        <p>
          For most data flowing through systems we build, our clients are the
          data <em>controllers</em> and ISD is the data <em>processor</em>.
          This policy describes how ISD itself handles information.
        </p>
      </LegalSection>

      <LegalSection n={2} title="Information we collect">
        <ul className="list-none space-y-2 pl-0">
          {[
            ["Contact information", "Name, email, company — when you email us or sign up for a plan."],
            ["Project content", "What you tell us about the systems you want built (in email or onboarding documents)."],
            ["Billing data", "Handled by our payment processor; we store invoice metadata, not card numbers."],
            ["Operational logs", "Aggregate telemetry from the systems we operate, used solely to keep them running."],
            ["Site analytics", "Privacy-respecting page-level analytics. No fingerprinting, no cross-site tracking."],
          ].map(([k, v]) => (
            <li key={k} className="flex gap-4 border-l border-white/10 pl-4">
              <span className="text-yellow-400 font-mono text-xs uppercase tracking-[0.22em] shrink-0 w-40 pt-1">{k}</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection n={3} title="What we never collect">
        <p>
          We don't collect message contents from end-users for any purpose
          other than operating the system your business has commissioned. We
          don't sell, rent, or share personal data with advertisers. We
          don't use customer conversations to train models.
        </p>
      </LegalSection>

      <LegalSection n={4} title="How long we keep it">
        <p>
          Email correspondence: as long as the engagement is active, plus
          24 months. Operational logs: 30 days by default (configurable per
          contract). Billing records: 7 years, where legally required.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Sub-processors">
        <p>
          We use a small, deliberate set of infrastructure providers
          (AWS, GCP, a payment processor, an email provider, an error
          monitoring service). A current list is available on request and is
          appended to enterprise DPAs.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Your rights">
        <p>
          You can ask us — at any time, for any reason — to access, correct,
          export, or delete any personal information we hold about you. Email
          us and we'll action it within 30 days, usually much faster.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Security">
        <p>
          AES-256 at rest, TLS 1.3 in transit, principle of least privilege,
          MFA enforced internally, quarterly access reviews, and an incident
          response plan with sub-5-minute initial response. Detailed
          security posture available under NDA.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Changes to this policy">
        <p>
          If we materially change this policy, we'll email known contacts at
          least 14 days before the change takes effect. The "Last updated"
          date at the top will always reflect the current version.
        </p>
      </LegalSection>

      <LegalSection n={9} title="Contact">
        <p>
          Questions or requests:{" "}
          <a
            href="mailto:Robert@InvisibleSystem.Design?subject=Privacy%20request"
            className="text-yellow-400 hover:underline"
            data-testid="privacy-contact-email"
          >
            Robert@InvisibleSystem.Design
          </a>
          . We answer every privacy request personally — async, like
          everything else we do.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
